import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Clock,
  MapPin,
  Calendar,
  CheckCircle2,
  XCircle,
  Timer,
} from 'lucide-react';

export const QCMyOffers = () => {
  const navigate = useNavigate();
  const { api } = useQC();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignments();
    const interval = setInterval(fetchAssignments, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/my/assignments');
      setAssignments(response.data || []);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (ackId) => {
    try {
      await api.post(`/assignments/${ackId}/acknowledge`);
      toast.success('Assignment acknowledged!');
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  const handleDecline = async (ackId) => {
    navigate(`/quickcanvass/decline/${ackId}`);
  };

  const pendingAssignments = assignments.filter(a => a.acknowledgement?.status === 'pending');
  const acceptedAssignments = assignments.filter(a => a.acknowledgement?.status === 'acknowledged');
  const declinedAssignments = assignments.filter(a => 
    a.acknowledgement?.status === 'declined' || a.acknowledgement?.status === 'expired'
  );

  const renderAssignment = (assignment, showActions = false) => (
    <Card key={assignment.id} className="hover:border-primary/50 transition-colors" data-testid={`assignment-card-${assignment.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-2">{assignment.job_title}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{assignment.crew_role_name}</Badge>
              <Badge variant={
                assignment.acknowledgement?.status === 'acknowledged' ? 'default' :
                assignment.acknowledgement?.status === 'pending' ? 'secondary' :
                assignment.acknowledgement?.status === 'declined' ? 'outline' :
                'destructive'
              }>
                {assignment.acknowledgement?.status || 'unknown'}
              </Badge>
              {assignment.assignment_source === 'forced_fill' && (
                <Badge variant="destructive">Forced Fill</Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {assignment.job_location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {assignment.job_location}
            </div>
          )}
          {assignment.job_start_date && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {assignment.job_start_date}
            </div>
          )}
        </div>

        {assignment.acknowledgement?.deadline && (
          <div className="flex items-center gap-2 text-sm">
            <Timer className="h-4 w-4 text-orange-500" />
            <span className="text-muted-foreground">
              Deadline: {new Date(assignment.acknowledgement.deadline).toLocaleString()}
            </span>
          </div>
        )}

        {showActions && assignment.acknowledgement?.status === 'pending' && (
          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => handleAcknowledge(assignment.acknowledgement.id)}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Accept
            </Button>
            <Button
              onClick={() => handleDecline(assignment.acknowledgement.id)}
              variant="outline"
              className="flex-1"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qc-my-offers">
      <Button variant="ghost" onClick={() => navigate('/quickcanvass')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">
          My Assignments
        </h1>
        <p className="text-muted-foreground mt-2">
          View and manage your job assignments
        </p>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="relative">
            Pending
            {pendingAssignments.length > 0 && (
              <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-orange-500">
                {pendingAssignments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="accepted">
            Accepted
            {acceptedAssignments.length > 0 && (
              <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-green-500">
                {acceptedAssignments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-6">
          {pendingAssignments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Timer className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No pending assignments</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {pendingAssignments.map(a => renderAssignment(a, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="accepted" className="space-y-4 mt-6">
          {acceptedAssignments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No accepted assignments</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {acceptedAssignments.map(a => renderAssignment(a, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-6">
          {declinedAssignments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No assignment history</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {declinedAssignments.map(a => renderAssignment(a, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QCMyOffers;
